import React, { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { apiUrl } from '../config/api';
import { AuthContext } from '../context/AuthContext';
// No sidebar here; user must complete password change before navigating

const ChangePassword = () => {
  const { register, handleSubmit, watch, formState: { errors, isSubmitting } } = useForm();
  const { user, setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [apiError, setApiError] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Live validation state
  const password = watch('newPassword') || '';
  const confirmPassword = watch('confirmPassword') || '';
  const rules = [
    { label: 'At least 8 characters long', ok: password.length >= 8 },
    { label: 'Contains at least one uppercase letter (A–Z)', ok: /[A-Z]/.test(password) },
    { label: 'Contains at least one lowercase letter (a–z)', ok: /[a-z]/.test(password) },
    { label: 'Contains at least one number (0–9)', ok: /[0-9]/.test(password) },
    { label: 'Contains at least one special character (e.g. ! @ # $ % ^ & *)', ok: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password) },
    { label: 'Passwords match', ok: confirmPassword.length > 0 && confirmPassword === password },
  ];

  const onSubmit = async (data) => {
    try {
      setApiError('');
  await axios.post(apiUrl('/api/change-password'), { newPassword: data.newPassword }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
  // Clear mustChangePassword locally so routing allows navigation
  setUser(prev => ({ ...prev, mustChangePassword: false }));
  const perms = Array.isArray(user.permissions) ? user.permissions : [];
  const isClient = perms.includes('SignDocuments') || perms.includes('ViewProfile');
  navigate(isClient ? '/dashboard' : '/admin');
    } catch (error) {
      const msg = error?.response?.data?.error || error?.response?.data?.message || 'Failed to update password. Please try again.';
      setApiError(msg);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950 flex items-start sm:items-center justify-center p-4">
      <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded shadow p-6">
        <h1 className="text-2xl font-bold mb-4 text-center text-gray-900 dark:text-slate-100">Change Password</h1>
        {apiError && (
          <div className="mb-4 p-3 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-sm">
            {apiError}
          </div>
        )}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="relative">
            <input
              {...register('newPassword', {
                required: 'New password is required',
                validate: (val) => {
                  if (!val || val.length < 8) return 'Password must be at least 8 characters long';
                  if (!/[A-Z]/.test(val)) return 'Password must include at least one uppercase letter';
                  if (!/[a-z]/.test(val)) return 'Password must include at least one lowercase letter';
                  if (!/[0-9]/.test(val)) return 'Password must include at least one number';
                  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(val)) return 'Password must include at least one special character';
                  return true;
                }
              })}
              type={showNew ? 'text' : 'password'}
              placeholder="New Password"
              className={`w-full px-3 py-2.5 pr-10 text-sm bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-500 rounded text-gray-900 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-400 focus:outline-none focus:ring-2 ${password ? '' : 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20'} transition-colors`}
            />
            <button
              type="button"
              aria-label={showNew ? 'Hide password' : 'Show password'}
              onClick={() => setShowNew(v => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-600 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200"
            >
              {showNew ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-5 0-9-4-9-4s1.5-2.25 4-3.75m3-1.5A9.973 9.973 0 0112 7c5 0 9 4 9 4s-.63.945-1.8 2.025M3 3l18 18" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
            {errors.newPassword && (
              <p className="text-rose-500 text-xs mt-1">{errors.newPassword.message}</p>
            )}
          </div>
          <div className="relative">
            <input
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: (val) => val === watch('newPassword') || 'Passwords do not match',
              })}
              type={showConfirm ? 'text' : 'password'}
              placeholder="Confirm New Password"
              className={`w-full pr-10 ${confirmPassword ? '' : 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20'}`}
            />
            <button
              type="button"
              aria-label={showConfirm ? 'Hide password' : 'Show password'}
              onClick={() => setShowConfirm(v => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-600 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200"
            >
              {showConfirm ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-5 0-9-4-9-4s1.5-2.25 4-3.75m3-1.5A9.973 9.973 0 0112 7c5 0 9 4 9 4s-.63.945-1.8 2.025M3 3l18 18" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
            {errors.confirmPassword && (
              <p className="text-rose-500 text-xs mt-1">{errors.confirmPassword.message}</p>
            )}
          </div>
          {/* Live requirements – below fields, ticks update as user types */}
          <div className="p-3 rounded border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-sm">
            Your new password must meet all of the following requirements:
            <ul className="mt-2 space-y-1">
              {rules.map((r, i) => (
                <li key={i} className={`flex items-center gap-2 ${r.ok ? 'text-green-700 dark:text-green-400' : 'text-gray-600 dark:text-slate-400'}`}>
                  <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold ${r.ok ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400' : 'bg-gray-200 dark:bg-slate-600 text-gray-600 dark:text-slate-400'}`}>
                    {r.ok ? '✓' : '•'}
                  </span>
                  <span>{r.label}</span>
                </li>
              ))}
            </ul>
          </div>
          <button type="submit" disabled={isSubmitting} className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white p-2 rounded w-full">Update Password</button>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;